/**
 * Shared API base for all frontend fetches.
 *
 * Defaults to the orchestrator Function App so the same build works when
 * served from the Function host, Azure Static Web Apps, or any other
 * origin. Override with VITE_API_BASE at build time.
 *
 * Auth: attaches 'Authorization: Bearer <entra token>' when a Microsoft
 * Entra ID session exists (preferred); otherwise falls back to the
 * meridian_session cookie (password login) during migration.
 */
import { getEntraToken } from '../auth/entra';

/**
 * TAB-SCOPED SESSIONS
 * sessionStorage is private to a single tab (it survives reloads in the same
 * tab but is never shared with other tabs). Keeping the password-login
 * session token there lets you sign in as different users in different tabs
 * side by side (e.g. super admin + a firm admin) with zero spillover: every
 * apiFetch from a tab carries that tab's own Bearer token, and the backend
 * is bearer-first, so the shared cookie can never override it. A brand-new
 * tab (no tab token) falls back to Entra, then to the cookie.
 */
const TAB_TOKEN_KEY = 'meridian_tab_token';

export function getTabToken(): string | null {
  try { return sessionStorage.getItem(TAB_TOKEN_KEY); } catch { return null; }
}
export function setTabToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(TAB_TOKEN_KEY, token);
    else sessionStorage.removeItem(TAB_TOKEN_KEY);
  } catch { /* storage unavailable */ }
}

export const API_BASE: string =
  (import.meta as any).env?.VITE_API_BASE ||
  'https://api.meridianhealthops.com/api';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: 'portfolio_admin' | 'tenant_admin' | 'ops';
  tenants: string[];
  firmId?: string;
}

/**
 * Fetch wrapper: sends an Entra bearer token when available, always sends
 * session cookies (the API may live on a different origin than the SPA),
 * and broadcasts a global event when the session expires so the app can
 * return to the login screen.
 */
export async function apiFetch(url: string, init: RequestInit = {}): Promise<Response> {
  // Precedence: this tab's own login token > Entra token > shared cookie.
  let token: string | null = getTabToken();
  if (!token) {
    try {
      token = await getEntraToken();
    } catch {
      // Entra not configured / unavailable: cookie auth still applies
    }
  }

  const headers = new Headers(init.headers || {});
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(url, {
    ...init,
    headers,
    // With a tab/bearer token we deliberately OMIT cookies so the shared
    // meridian_session cookie can never bleed another tab's login into
    // this tab's requests.
    credentials: token ? 'omit' : 'include',
  });
  if (res.status === 401) {
    window.dispatchEvent(new Event('meridian:unauthorized'));
  }
  return res;
}
